import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { rgba, media } from '../styles/stylesTheme';
import { getCSSImageUrl } from '../utils/mediaUrl';

const ModalWordGlossary = ({
    glossaryData,
    isOpen,
    onClose,
    isLoading,
}) => {
    const handleBackdropClick = e => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Handle ESC key to close modal
    useEffect(() => {
        const handleKeyDown = e => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    return (
        <AnimatePresence mode="wait">
            {isOpen && (
                <Backdrop
                    onClick={handleBackdropClick}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <CloseButton
                        onClick={onClose}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.2 }}
                        whileTap={{ scale: 0.95 }}
                    />
                    <ModalContainer
                        onClick={e => e.stopPropagation()}
                        initial={{
                            opacity: 0,
                            y: 20,
                        }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{
                            opacity: 0,
                            y: 20,
                            transition: {
                                y: {
                                    type: 'tween',
                                    duration: 0.3,
                                    ease: 'easeIn',
                                },
                                opacity: {
                                    duration: 0.2,
                                },
                            },
                        }}
                        transition={{
                            y: {
                                type: 'spring',
                                bounce: 1,
                                stiffness: 300,
                                damping: 16,
                                duration: 0.3,
                            },
                            opacity: {
                                duration: 0.2,
                                ease: 'easeInOut',
                            },
                        }}
                    >
                        <AnimatePresence>
                            {isLoading ? (
                                <ContentContainer
                                    as={motion.div}
                                    key="skeleton"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            delay: 0.1,
                                            duration: 0.4,
                                        }}
                                    >
                                        <Header $isLoading>
                                            <WordTitleContainer>
                                                <SkeletonTitle />
                                            </WordTitleContainer>
                                            <SkeletonTranslation />
                                            <SkeletonWordType />
                                        </Header>
                                    </motion.div>

                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            delay: 0.2,
                                            duration: 0.4,
                                        }}
                                    >
                                        <Section>
                                            <SkeletonSectionTitle />
                                            <VariationsGrid
                                                style={{ marginTop: '0.75em' }}
                                            >
                                                {[1, 2, 3, 4].map(i => (
                                                    <SkeletonVariationItem
                                                        key={i}
                                                    >
                                                        <SkeletonVariationLabel />
                                                        <SkeletonVariationValue />
                                                        <SkeletonVariationTranslation />
                                                    </SkeletonVariationItem>
                                                ))}
                                            </VariationsGrid>
                                        </Section>
                                    </motion.div>

                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            delay: 0.3,
                                            duration: 0.4,
                                        }}
                                    >
                                        <Section>
                                            <SkeletonSectionTitleAlt1 />
                                            {[1, 2, 3].map(i => (
                                                <SkeletonPhraseItem key={i}>
                                                    <SkeletonPhraseText />
                                                    <SkeletonPhraseTranslation />
                                                </SkeletonPhraseItem>
                                            ))}
                                        </Section>
                                    </motion.div>
                                </ContentContainer>
                            ) : glossaryData ? (
                                <ContentContainer
                                    as={motion.div}
                                    key="content"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <Header>
                                        <WordTitleContainer>
                                            <WordTitle>
                                                {glossaryData.word}
                                            </WordTitle>
                                        </WordTitleContainer>
                                        <Translation>
                                            {glossaryData.translation}
                                        </Translation>
                                        <WordType>
                                            {glossaryData.wordType}
                                        </WordType>
                                    </Header>

                                    {/* Variations Section */}
                                    {glossaryData.variations &&
                                        glossaryData.variations.length > 0 && (
                                            <Section>
                                                <SectionTitle>
                                                    Variations
                                                </SectionTitle>
                                                <VariationsGrid>
                                                    {glossaryData.variations.map(
                                                        (variation, index) => (
                                                            <VariationItem
                                                                key={index}
                                                            >
                                                                <VariationLabel>
                                                                    {
                                                                        variation.label
                                                                    }
                                                                </VariationLabel>
                                                                <VariationWrap>
                                                                    <VariationValue>
                                                                        {
                                                                            variation.form
                                                                        }
                                                                    </VariationValue>
                                                                    {variation.translation && (
                                                                        <VariationTranslation>
                                                                            {
                                                                                variation.translation
                                                                            }
                                                                        </VariationTranslation>
                                                                    )}
                                                                </VariationWrap>
                                                            </VariationItem>
                                                        )
                                                    )}
                                                </VariationsGrid>
                                            </Section>
                                        )}

                                    {/* Similar Phrases */}
                                    {glossaryData.similarPhrases &&
                                        glossaryData.similarPhrases.length >
                                            0 && (
                                            <Section>
                                                <SectionTitle>
                                                    Similar Phrases
                                                </SectionTitle>
                                                <PhraseList>
                                                    {glossaryData.similarPhrases.map(
                                                        (item, index) => (
                                                            <PhraseItem
                                                                key={index}
                                                            >
                                                                <Phrase>
                                                                    {
                                                                        item.phrase
                                                                    }
                                                                </Phrase>
                                                                <PhraseTranslation>
                                                                    {
                                                                        item.translation
                                                                    }
                                                                </PhraseTranslation>
                                                            </PhraseItem>
                                                        )
                                                    )}
                                                </PhraseList>
                                            </Section>
                                        )}

                                    {/* Example Sentences */}
                                    {glossaryData.exampleSentences &&
                                        glossaryData.exampleSentences.length >
                                            0 && (
                                            <Section>
                                                <SectionTitle>
                                                    Example Sentences
                                                </SectionTitle>
                                                <ExampleList>
                                                    {glossaryData.exampleSentences.map(
                                                        (item, index) => (
                                                            <ExampleItem
                                                                key={index}
                                                            >
                                                                <Sentence>
                                                                    {
                                                                        item.sentence
                                                                    }
                                                                </Sentence>
                                                                <SentenceTranslation>
                                                                    {
                                                                        item.translation
                                                                    }
                                                                </SentenceTranslation>
                                                            </ExampleItem>
                                                        )
                                                    )}
                                                </ExampleList>
                                            </Section>
                                        )}
                                </ContentContainer>
                            ) : (
                                <ErrorContainer
                                    as={motion.div}
                                    key="error"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <ErrorText>
                                        Failed to load word glossary
                                    </ErrorText>
                                </ErrorContainer>
                            )}
                        </AnimatePresence>
                    </ModalContainer>
                </Backdrop>
            )}
        </AnimatePresence>
    );
};

const Backdrop = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    //background: ${props => rgba(props.theme, 'gradL', 0.2)};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 110000;
    padding: 1em;
    //backdrop-filter: blur(0.5em);
    //-webkit-backdrop-filter: blur(0.5em);
`;

const CloseButton = styled(motion.button)`
    position: fixed;
    width: 3em;
    height: 3em;
    border: none;
    background: none;
    cursor: pointer;
    z-index: 10001;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease;
    background: ${props => rgba(props.theme, 'black', 0.05)};
    display: none;
    top: 2em;
    right: 2em;

    &:hover {
        background: ${props => rgba(props.theme, 'black', 0.07)};
    }

    &:active {
        transform: scale(0.95);
    }

    &::before {
        content: '';
        width: 1.2em;
        height: 1.2em;
        background: ${props => rgba(props.theme, 'black', 1)};
        mask-image: url('${getCSSImageUrl("img/icon-x.svg")}');
        -webkit-mask-image: url('${getCSSImageUrl("img/icon-x.svg")}');
        mask-position: center;
        -webkit-mask-position: center;
        mask-repeat: no-repeat;
        -webkit-mask-repeat: no-repeat;
        mask-size: contain;
        -webkit-mask-size: contain;
        display: block;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translateY(-50%) translateX(-50%);
    }

    ${media.tablet} {
        top: 1.6em;
        right: 1.6em;
    }

    ${media.mobile} {
        top: 2.4em;
        right: 2.4em;
        display: block;
    }
`;

const ModalContainer = styled(motion.div)`
    position: relative;
    background: ${props => rgba(props.theme, 'gradL', 1)};
    border-radius: 16px;
    padding: 2em;
    max-width: 40em;
    width: 100%;
    height: 69vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);

    * {
        box-sizing: border-box;
    }

    ${media.mobile} {
        padding: 1.5em;
        height: calc(100svh - 5em);
        max-width: none;
        width: calc(100vw - 5em);
        font-size: 1.4em;
    }
`;

const SkeletonBlock = styled.div`
    background: ${props => rgba(props.theme, 'black', 0.07)};
    border-radius: 0.4em;
    animation: pulse 1.5s ease-in-out infinite;

    @keyframes pulse {
        0%,
        100% {
            opacity: 1;
        }
        50% {
            opacity: 0.5;
        }
    }
`;

const SkeletonTitle = styled(SkeletonBlock)`
    width: 60%;
    height: 2.5em;
`;

const SkeletonTranslation = styled(SkeletonBlock)`
    width: 45%;
    height: 1.2em;
    margin-bottom: 0.5em;
`;

const SkeletonWordType = styled(SkeletonBlock)`
    width: 25%;
    height: 0.9em;
    margin-top: 0.3em;
`;

const SkeletonSectionTitle = styled(SkeletonBlock)`
    width: 30%;
    height: 1.5em;
    margin-top: 2em;
`;

const SkeletonSectionTitleAlt1 = styled(SkeletonBlock)`
    width: 35%;
    height: 1.5em;
    margin-top: 2em;
`;

const SkeletonVariationLabel = styled(SkeletonBlock)`
    width: 50%;
    height: 0.8em;
`;

const SkeletonVariationValue = styled(SkeletonBlock)`
    width: 80%;
    height: 1.2em;
    margin-top: 0.6em;
`;

const SkeletonVariationTranslation = styled(SkeletonBlock)`
    width: 60%;
    height: 0.9em;
    margin-top: 0.3em;
`;

const SkeletonPhraseText = styled(SkeletonBlock)`
    width: 70%;
    height: 1em;
    margin-top: 0.6em;
`;

const SkeletonPhraseTranslation = styled(SkeletonBlock)`
    width: 55%;
    height: 0.85em;
    margin-top: 0.3em;
    margin-bottom: 0.6em;
`;

const SkeletonVariationItem = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 0.4em;
    background: ${props => rgba(props.theme, 'black', 0.05)};
    padding: 1em;
`;

const SkeletonPhraseItem = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 0.4em;
    background: ${props => rgba(props.theme, 'black', 0.05)};
    padding: 1em;
    margin-top: 0.75em;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    position: absolute;
    width: 100%;
    top: 0;
    left: 0;
    padding: 2em;

    ${media.mobile} {
        padding-top: 6em;
    }
`;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.3em;
    color: ${props => rgba(props.theme, 'colortextYou', 1)};
    position: relative;
`;

const WordTitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5em;
    position: relative;
`;

const WordTitle = styled.h2`
    font-size: 2em;
    font-family: var(--bold);
    color: ${props => rgba(props.theme, 'colortextYou', 1)};
    margin: 0;
    line-height: 1.25em;
    margin-top: -0.1em;
`;

const Translation = styled.p`
    font-size: 1.2em;
    color: ${props => rgba(props.theme, 'colortextYou', 0.6)};
    margin: -0.3em 0 0;
    font-family: var(--med);
    line-height: 1.25em;
`;

const WordType = styled.div`
    font-size: 0.85em;
    color: ${props => rgba(props.theme, 'colortextYou', 0.5)};
    font-style: italic;
    text-transform: capitalize;
    margin-top: -0.4em;

    ${media.mobile} {
        margin-top: 0;
    }
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75em;
`;

const SectionTitle = styled.h3`
    font-size: 1.1em;
    font-weight: 600;
    color: ${props => rgba(props.theme, 'colortextYou', 1)};
    margin: 2em 0 0;
    font-family: var(--bold);
`;

const VariationsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1em;
    border-radius: 0.7em;
`;

const VariationItem = styled.div`
    display: flex;
    flex-direction: column;
    color: ${props => rgba(props.theme, 'colortextYou', 0.6)};
    border-radius: 0.4em;
    background: ${props => rgba(props.theme, 'black', 0.07)};
    position: relative;
`;

const VariationLabel = styled.div`
    font-size: 0.8em;
    color: ${props => rgba(props.theme, 'colortextYou', 0.6)};
    background: ${props => rgba(props.theme, 'black', 0.08)};
    padding: 0.7em 1em;
    font-family: var(--med);
    border-top-left-radius: 0.5em;
    border-top-right-radius: 0.5em;
    line-height: 1.35em;
`;

const VariationValue = styled.div`
    font-size: 1em;
    color: ${props => rgba(props.theme, 'colortextYou', 1)};
    font-family: var(--med);
    padding: 0.7em 0.8em 0.3em;
    line-height: 1.4;
    transition: transform 0.2s ease;
    position: relative;
`;

const VariationTranslation = styled.div`
    font-size: 0.85em;
    color: ${props => rgba(props.theme, 'colortextYou', 0.6)};
    font-style: italic;
    padding: 0em 0.95em 1em;
    line-height: 1.4;
`;

const PhraseList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75em;
`;

const PhraseItem = styled.div`
    display: flex;
    flex-direction: column;
    color: ${props => rgba(props.theme, 'colortextYou', 0.6)};
    border-radius: 0.4em;
    background: ${props => rgba(props.theme, 'black', 0.1)};
    position: relative;
`;

const Phrase = styled.div`
    font-size: 1em;
    color: ${props => rgba(props.theme, 'colortextYou', 1)};
    font-family: var(--med);
    padding: 0.7em 0.9em 0.2em;
    line-height: 1.4;
`;

const PhraseTranslation = styled.div`
    font-size: 0.85em;
    color: ${props => rgba(props.theme, 'colortextYou', 0.6)};
    font-style: italic;
    padding: 0em 1em 0.9em;
    line-height: 1.4;
`;

const ExampleList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75em;
`;

const ExampleItem = styled.div`
    display: flex;
    flex-direction: column;
    color: ${props => rgba(props.theme, 'colortextYou', 0.6)};
    border-radius: 0.4em;
    background: ${props => rgba(props.theme, 'black', 0.1)};
    position: relative;
`;

const Sentence = styled.div`
    font-size: 1em;
    color: ${props => rgba(props.theme, 'colortextYou', 1)};
    font-family: var(--med);
    padding: 0.7em 0.9em 0.2em;
    line-height: 1.4;
`;

const SentenceTranslation = styled.div`
    font-size: 0.85em;
    color: ${props => rgba(props.theme, 'colortextYou', 0.6)};
    font-style: italic;
    padding: 0em 1em 0.9em;
    line-height: 1.4;
`;

const ErrorContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3em 0;
`;

const ErrorText = styled.p`
    color: ${props => rgba(props.theme, 'colortextYou', 0.6)};
    font-size: 1em;
`;

const VariationWrap = styled.div`
    position: relative;
`;

export default ModalWordGlossary;
